import { App, View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  PMSmallNumber,
  PMSmallNumberCardEditSchema,
  PMSmallNumberProps,
} from "@pimo/pimo-components";

import { TestAppState } from "../../../app";
import { getLocalizedCardCopy } from "../../../utils/get-localized-card-copy";
import {
  getItemName,
  getPluralOfItemName,
} from "../../../utils/get-plural-of-item-name";
import { getProjectName } from "../../../utils/get-plural-of-project-name";
import { isFieldEnabled } from "../../field-enabled";
import { makeGetCardCopy } from "../card-copy";

/**
 * Adds the number-of-completed-items and the completed-items-ratio cards, in
 * that order (#2222).
 */
export function addCompletedItemsCards(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
) {
  const language = app.getAppState().language || "en";
  const getCardCopy = makeGetCardCopy(app);

  const numberOfCompletedItemsComponent = view.addComponent({
    component: PMSmallNumber,
    layoutProps: {
      componentId: "numberOfCompletedItemsComponent",
      labels: ["#ofItems"],
      description: getCardCopy(language, "reportNumberOfCompletedItems")
        .description,
      title: getCardCopy(language, "reportNumberOfCompletedItems").title,
      editSchema: PMSmallNumberCardEditSchema,
    },
  });

  numberOfCompletedItemsComponent.mapState(
    ({ currentReport, reportComponentValues, ...appState }) => {
      const a: PMSmallNumberProps | undefined = reportComponentValues?.[
        "numberOfCompletedItemsComponent"
      ] as PMSmallNumberProps | undefined;
      return {
        showInfoIcon: false,
        showMenuIcon: false,
        title:
          a?.title ??
          getLocalizedCardCopy(
            appState.language || "en",
            "reportNumberOfCompletedItems",
            {
              itemName: getItemName(
                appState.settings?.configuration?.itemName,
                appState.language || "en"
              ),
              itemNamePlural: getPluralOfItemName(
                appState.settings?.configuration?.itemName,
                appState.language || "en"
              ),
              projectName: getProjectName(
                appState.settings?.configuration?.projectName,
                appState.language || "en"
              ),
            }
          ).title,
        number:
          a?.number ??
          currentReport?.project?._calculated?.numberOfClosedSubEntities ??
          0,
      };
    }
  );

  numberOfCompletedItemsComponent.mapVisibility(({ settings }) =>
    isFieldEnabled(settings, "completion", "itemReporting")
  );

  const ratioNumberOfCompletedItemsComponent = view.addComponent({
    component: PMSmallNumber,
    layoutProps: {
      componentId: "ratioNumberOfCompletedItemsComponent",
      labels: ["#ratioNumberOfCompletedItemsComponent"],
      description: getCardCopy(language, "reportNumberOfCompletedItems")
        .description,
      title: getCardCopy(language, "reportItemCompletionCounts").title,
      editSchema: PMSmallNumberCardEditSchema,
    },
  });

  ratioNumberOfCompletedItemsComponent.mapState(
    ({ currentReport, ...appState }) => {
      return {
        showInfoIcon: false,
        showMenuIcon: false,
        title: getLocalizedCardCopy(
          appState.language || "en",
          "reportItemCompletionCounts",
          {
            itemName: getItemName(
              appState.settings?.configuration?.itemName,
              appState.language || "en"
            ),
            itemNamePlural: getPluralOfItemName(
              appState.settings?.configuration?.itemName,
              appState.language || "en"
            ),
            projectName: getProjectName(
              appState.settings?.configuration?.projectName,
              appState.language || "en"
            ),
          }
        ).title,
        number: `${
          currentReport?.project?._calculated?.numberOfClosedSubEntities ?? 0
        }/${currentReport?.project?._calculated?.numberOfSubEntities ?? 0}`,
      };
    }
  );

  ratioNumberOfCompletedItemsComponent.mapVisibility(({ settings }) =>
    isFieldEnabled(settings, "completion", "itemReporting")
  );
}
